/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import {
  keepLatestTask,
  restartableTask,
  task,
  timeout,
} from 'ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import clientsQuery from 'my-phorest/gql/queries/clients.graphql';
import { action } from '@ember/object';
import { variation } from 'ember-launch-darkly';
import {
  fetchClientSettings,
  handleDuplicateMobile,
} from 'my-phorest/utils/client-creation';
import {
  createClient,
  validationModel,
} from 'my-phorest/utils/quick-client-creation.js';
import { waitForProperty } from 'ember-concurrency';

const SEARCH_DEBOUNCE_MS = 250;

export default class ClientsSearchComponent extends Component {
  @service clientSearch;
  @service session;
  @service intl;
  @service marketingConsentModal;

  @queryManager apollo;

  clientsQuery = clientsQuery;

  @tracked isNewClientClicked = false;
  @tracked linkedClient = null;

  constructor() {
    super(...arguments);

    if (!variation('release-quick-client-creation')) return;
    this.queryClientDataTask.perform();
  }

  get searchProperties() {
    return this.clientSearch.searchProperties;
  }

  get filters() {
    const filters = {};
    const { firstName, lastName, mobile, email } = this.searchProperties;

    if (firstName) {
      filters.firstName = firstName;
    }
    if (lastName) {
      filters.lastName = lastName;
    }
    if (mobile) {
      filters.phone = mobile;
    }
    if (email) {
      filters.email = email;
    }

    let { showClientsForAllBranches } = this.session.currentBranch;
    if (
      typeof showClientsForAllBranches === 'boolean' &&
      !showClientsForAllBranches
    ) {
      filters.branchId = this.session.currentBranch.id;
    }

    return filters;
  }

  get hasSearchInput() {
    return this.clientSearch.isSearchPropertiesPresent;
  }

  get queryVariables() {
    return {
      orderBy: [
        { field: 'FIRST_NAME', direction: 'ASC' },
        { field: 'LAST_NAME', direction: 'ASC' },
      ],
      filterBy: this.filters,
    };
  }

  @restartableTask
  *fetchClientsTask(variables = {}) {
    return yield this.apollo.watchQuery(
      {
        query: clientsQuery,
        variables,
      },
      'clients'
    );
  }

  @restartableTask
  *searchTask(fieldName, resetFn, event) {
    this.clientSearch.searchProperties = {
      ...this.searchProperties,
      [fieldName]: event.target.value,
    };

    yield timeout(SEARCH_DEBOUNCE_MS);
    yield resetFn();
  }

  @task
  *onLinkClickTask(form) {
    let isValid = yield form.validate();
    if (isValid) {
      let savedClient = yield createClient(
        this.apollo,
        this.marketingConsentModal,
        this.clientSettings,
        this.linkedClient,
        this.searchProperties
      );
      this.args.onClientChoose(savedClient);
      return savedClient;
    }
  }

  @keepLatestTask
  *triggerClientCreationTask(_event, revalidate) {
    if (!variation('release-quick-client-creation')) {
      this.args.onCreateClient();
      return;
    }
    yield waitForProperty(this, 'clientSettings');
    let { duplicationWasHandled, returnedLinkedClient } =
      yield handleDuplicateMobile(
        revalidate,
        this.apollo,
        this.intl,
        this.clientSettings,
        this.linkedClient,
        this.searchProperties.mobile
      );

    if (duplicationWasHandled) {
      this.linkedClient = returnedLinkedClient;
      return;
    }

    let savedClient = yield createClient(
      this.apollo,
      this.marketingConsentModal,
      this.clientSettings,
      null,
      this.searchProperties
    );
    this.args.onClientChoose(savedClient);
    return savedClient;
  }

  @action
  onInput(property, resetFn, form, event) {
    this.searchTask.perform(property, resetFn, event);
    form.validate();
    this.linkedClient = null;
  }

  get validationSchema() {
    return this.queryClientDataTask.lastSuccessful?.value;
  }

  get validationModel() {
    return validationModel(this.searchProperties);
  }

  @keepLatestTask
  *queryClientDataTask() {
    let { validationSchema, clientSettings } = yield fetchClientSettings(
      this.apollo,
      true
    );

    this.clientSettings = clientSettings;
    return validationSchema;
  }

  @action
  handleEnterPress(event) {
    if (event.key === 'Enter') {
      const firstClient =
        this.fetchClientsTask.lastSuccessful?.value?.edges?.[0]?.node;
      if (firstClient) {
        event.preventDefault();
        this.args.onClientChoose(firstClient);
      }
    }
  }
}
